/**
 * CaseComplaintsTab — Case-scoped complaint list with officer note thread
 *
 * 7th tab in CaseDetailPage.
 * Shows complaints linked to this specific case, with note thread expansion.
 */
import React, { useState, useEffect, useCallback } from "react";
import { api } from "../services/api.js";
import { getCurrentUserId } from "../services/auth.js";

interface OfficerNote { text: string; author_id: string; created_at: string; }
interface Complaint {
  complaint_id: string;
  case_id: string;
  raised_by: string;
  category: string;
  description: string;
  status: "open" | "in_review" | "resolved";
  officer_notes: OfficerNote[];
  created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  open: "#dc2626",
  in_review: "#d97706",
  resolved: "#16a34a",
};

const TH: React.CSSProperties = {
  padding: "10px 14px", textAlign: "left", fontWeight: 600,
  fontSize: 12, color: "var(--muted, #64748b)", textTransform: "uppercase",
  letterSpacing: "0.05em", borderBottom: "1px solid var(--border, #e2e8f0)",
  background: "var(--panel, #f8fafc)",
};
const TD: React.CSSProperties = {
  padding: "10px 14px", fontSize: 14, color: "var(--ink, #0f172a)",
  borderBottom: "1px solid var(--border, #e2e8f0)", verticalAlign: "top",
};

export function CaseComplaintsTab({ caseId }: { caseId: string }): React.ReactElement {
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");
  const [submittingNote, setSubmittingNote] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newForm, setNewForm] = useState({ raised_by: "", category: "process_dispute", description: "" });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.complaints.list({ case_id: caseId });
      setComplaints(res.data as unknown as Complaint[]);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [caseId]);

  useEffect(() => { void load(); }, [load]);

  async function submitNote(complaintId: string): Promise<void> {
    if (!noteText.trim()) return;
    setSubmittingNote(true);
    setNoteError(null);
    try {
      await api.complaints.addNote(complaintId, noteText.trim(), getCurrentUserId() ?? "unknown");
      setNoteText("");
      await load();
    } catch (e) {
      setNoteError(e instanceof Error ? e.message : "Failed to add note");
    } finally { setSubmittingNote(false); }
  }

  async function createComplaint(): Promise<void> {
    if (!newForm.raised_by.trim() || !newForm.description.trim()) return;
    setCreating(true);
    try {
      await api.complaints.create({ case_id: caseId, ...newForm });
      setShowCreate(false);
      setNewForm({ raised_by: "", category: "process_dispute", description: "" });
      await load();
    } catch { /* silent */ }
    finally { setCreating(false); }
  }

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>
        Loading complaints…
      </div>
    );
  }

  return (
    <div style={{ padding: "0 4px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>
          Complaints ({complaints.length})
        </h3>
        <button
          id="btn-log-complaint"
          onClick={() => setShowCreate((v) => !v)}
          style={{
            padding: "7px 14px", background: "var(--brand, #6366f1)", color: "#fff",
            border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600,
          }}
        >
          + Log Complaint
        </button>
      </div>

      {/* New complaint form */}
      {showCreate && (
        <div style={{
          background: "var(--panel, #f8fafc)", border: "1px solid var(--border, #e2e8f0)",
          borderRadius: 8, padding: 16, marginBottom: 20,
        }}>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)", display: "block", marginBottom: 4 }}>Raised By (Participant ID / Name)</label>
            <input
              value={newForm.raised_by} onChange={(e) => setNewForm((f) => ({ ...f, raised_by: e.target.value }))}
              style={{ width: "100%", padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 14, boxSizing: "border-box" }}
              placeholder="e.g. BORRW-001 or Borrower name"
            />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)", display: "block", marginBottom: 4 }}>Category</label>
            <select
              value={newForm.category} onChange={(e) => setNewForm((f) => ({ ...f, category: e.target.value }))}
              style={{ width: "100%", padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 14, background: "#fff" }}
            >
              <option value="process_dispute">Process Dispute</option>
              <option value="valuation_dispute">Valuation Dispute</option>
              <option value="timeline_concern">Timeline Concern</option>
              <option value="misconduct">Misconduct</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)", display: "block", marginBottom: 4 }}>Description</label>
            <textarea
              value={newForm.description} onChange={(e) => setNewForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              style={{ width: "100%", padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 14, boxSizing: "border-box", resize: "vertical" }}
              placeholder="Describe the complaint in detail…"
            />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => { void createComplaint(); }}
              disabled={creating}
              style={{ padding: "8px 16px", background: "var(--brand, #6366f1)", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
            >
              {creating ? "Saving…" : "Submit"}
            </button>
            <button onClick={() => setShowCreate(false)} style={{ padding: "8px 16px", border: "1px solid var(--border)", borderRadius: 6, cursor: "pointer", background: "#fff" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {complaints.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--muted)", border: "1px solid var(--border)", borderRadius: 8 }}>
          No complaints recorded for this case.
        </div>
      ) : (
        <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid var(--border, #e2e8f0)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={TH}>ID</th>
                <th style={TH}>Raised By</th>
                <th style={TH}>Category</th>
                <th style={TH}>Status</th>
                <th style={TH}>Date</th>
                <th style={TH}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {complaints.map((c) => (
                <React.Fragment key={c.complaint_id}>
                  <tr
                    style={{ cursor: "pointer", background: expanded === c.complaint_id ? "var(--panel, #f8fafc)" : "#fff" }}
                    onClick={() => setExpanded(expanded === c.complaint_id ? null : c.complaint_id)}
                  >
                    <td style={{ ...TD, fontFamily: "monospace", fontSize: 12 }}>{c.complaint_id}</td>
                    <td style={TD}>{c.raised_by}</td>
                    <td style={TD}>{c.category.replace(/_/g, " ")}</td>
                    <td style={TD}>
                      <span style={{
                        background: `${STATUS_COLORS[c.status] ?? "#64748b"}18`,
                        color: STATUS_COLORS[c.status] ?? "#64748b",
                        borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                      }}>
                        {c.status.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td style={TD}>{new Date(c.created_at).toLocaleDateString("en-GB")}</td>
                    <td style={{ ...TD, color: "var(--muted)" }}>{c.officer_notes.length} note{c.officer_notes.length !== 1 ? "s" : ""} ▾</td>
                  </tr>
                  {expanded === c.complaint_id && (
                    <tr>
                      <td colSpan={6} style={{ padding: "0 0 12px 0", background: "var(--panel, #f8fafc)" }}>
                        <div style={{ padding: "14px 20px" }}>
                          <p style={{ margin: "0 0 12px", fontSize: 14, color: "var(--ink)", lineHeight: 1.5 }}>
                            <strong>Description:</strong> {c.description}
                          </p>
                          <div style={{ marginBottom: 12 }}>
                            <strong style={{ fontSize: 12, color: "var(--muted)", textTransform: "uppercase" }}>Officer Notes ({c.officer_notes.length})</strong>
                            {c.officer_notes.map((n, i) => (
                              <div key={i} style={{ marginTop: 8, padding: "8px 12px", background: "#fff", borderLeft: "3px solid var(--brand, #6366f1)", borderRadius: 4 }}>
                                <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 2 }}>{n.author_id} · {new Date(n.created_at).toLocaleString("en-GB")}</div>
                                <div style={{ fontSize: 13, color: "var(--ink)" }}>{n.text}</div>
                              </div>
                            ))}
                          </div>
                          <div style={{ display: "flex", gap: 8 }}>
                            <textarea
                              value={noteText} onChange={(e) => setNoteText(e.target.value)}
                              rows={2} placeholder="Add officer note…"
                              style={{ flex: 1, padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 13, resize: "vertical" }}
                            />
                            <button
                              onClick={() => { void submitNote(c.complaint_id); }}
                              disabled={submittingNote || !noteText.trim()}
                              style={{
                                padding: "0 16px", background: "var(--brand, #6366f1)", color: "#fff",
                                border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13,
                              }}
                            >
                              {submittingNote ? "…" : "Add"}
                            </button>
                          </div>
                          {noteError && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 6 }}>{noteError}</div>}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
